import { HTTPClientInterface, HTTPRequest } from '@shared/core/HTTPClientInterface';
import { buildURL, SystemURL } from "./URLCatalog";

export interface HourBankEntry {
    id: string
    presenteeismRecordID: string
    userID: string
    organizationID: string
    shiftRecipeID?: string
    timestamp: number
    minutesWorked: number
    minutesExpected: number
    minutesDifference: number
    entryType: "REGULAR" | "CREDIT" | "MANUAL" | "DEBIT-USE"
    reason?: string
    motive?: string

}

export interface HourBankSummaryEntry {
    name: string
    image: string
    minutesWorked: number
    minutesExpected: number
    minutesDifference: number
}

export interface HourBankSummary {
    [userID: string]: HourBankSummaryEntry
}

export interface HourBankBalanceEntryInput {
    date: string
    workedMinutes: number
    motive: string
}

export class HourBankService {
    private httpClient: HTTPClientInterface;

    constructor(httpClient: HTTPClientInterface) {
        this.httpClient = httpClient;
    }

    async getHourBankSummary(orgID: string): Promise<HourBankSummary> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Dashboard.getHourBankSummary, { org_id: orgID });
        request.method = "GET";

        const response = await this.httpClient.execute(request);
        return response.result as HourBankSummary;
    }

    async getHourBankSummaryForDateRange(orgID: string, startDate: string, endDate: string): Promise<HourBankSummary> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Dashboard.getHourBankSummaryDateRange, {
            org_id: orgID,
            startDate,
            endDate,
        });
        request.method = "GET";

        const response = await this.httpClient.execute(request);
        return response.result as HourBankSummary;
    }

    async getHourBankColaboratorEntries(orgID: string, collaboratorID: string): Promise<HourBankEntry[]> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Dashboard.getHourBankCollaboratorEntries, {
            org_id: orgID,
            collaborator_id: collaboratorID,
        });
        request.method = "GET";

        const response = await this.httpClient.execute(request);
        const entries = (response.result ?? []) as Array<Partial<HourBankEntry> & {
            sourcePresenteeismRecordID?: string;
            entryType?: "REGULAR" | "CREDIT" | "MANUAL" | "DEBIT-USE";
        }>;

        return entries.map((entry) => ({
            ...entry,
            id: entry.id ?? "",
            presenteeismRecordID: entry.presenteeismRecordID ?? entry.sourcePresenteeismRecordID ?? "",
            userID: entry.userID ?? "",
            organizationID: entry.organizationID ?? "",
            timestamp: entry.timestamp ?? 0,
            minutesWorked: entry.minutesWorked ?? 0,
            minutesExpected: entry.minutesExpected ?? 0,
            minutesDifference: entry.minutesDifference ?? 0,
            entryType: entry.entryType ?? "REGULAR",
            motive: entry.motive ?? entry.reason ?? "",
        })) as HourBankEntry[];
    }

    async addHourBankBalanceEntry(
        orgID: string,
        collaboratorID: string,
        date: string,
        workedMinutes: number,
        motive: string
    ): Promise<null> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Dashboard.postHourBankCollaboratorEntry, {
            org_id: orgID,
            collaborator_id: collaboratorID,
        });
        request.method = "POST";
        request.body = {
            date,
            workedMinutes,
            motive,
            
        };

        await this.httpClient.execute(request);
        return null;
    }
}

export default HourBankService
